/**
 * Newsletter form: optional direct-to-Klaviyo submission.
 *
 * Wraps the newsletter-form block. When the block is set to send sign-ups
 * to a Klaviyo list, this intercepts the submit and posts the email (and
 * first name) to Klaviyo's public client subscriptions API, which needs
 * only the account's public key and the list ID.
 *
 * Fail-safe behaviour, so a sign-up is never dropped and the visitor never
 * sees an error for something they can't fix:
 *  - IDs missing or malformed, or JavaScript unavailable: nothing is
 *    intercepted; the form posts to Shopify's customer form as normal.
 *  - Klaviyo request blocked (ad/tracker blockers), rejected, or timed out:
 *    the form is handed to Shopify instead. Shopify then shows its own
 *    success message, and the Klaviyo integration syncs the customer.
 *  - Only if that hand-off itself is impossible is the error message shown.
 */

const ID_PATTERN = /^[A-Za-z0-9]{6}$/;
const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
const KLAVIYO_REVISION = '2024-10-15';
const REQUEST_TIMEOUT_MS = 10000;

class NewsletterFormComponent extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;
  #busy = false;

  connectedCallback() {
    this.#form = this.querySelector('form');
    if (!this.#form) return;

    const { listId, companyId } = this.dataset;
    if (!ID_PATTERN.test(listId ?? '') || !ID_PATTERN.test(companyId ?? '')) {
      // Fail closed: leave the Shopify form untouched.
      return;
    }

    this.#form.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#handleSubmit);
  }

  #handleSubmit = async (event) => {
    event.preventDefault();
    if (this.#busy || !this.#form) return;

    const emailInput = this.#form.querySelector('input[name="contact[email]"]');
    const nameInput = this.#form.querySelector('input[name="contact[first_name]"]');
    const button = this.#form.querySelector('button');

    if (!(emailInput instanceof HTMLInputElement)) return;
    if (!this.#form.reportValidity()) return;

    const email = emailInput.value.trim();
    const firstName = nameInput instanceof HTMLInputElement ? nameInput.value.trim() : '';

    this.#setBusy(true, button);
    this.#showStatus(null);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(`${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(this.dataset.companyId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(this.#buildPayload(email, firstName)),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Klaviyo responded with status ${response.status}`);
      }

      clearTimeout(timeout);
      this.#form.reset();
      this.#showStatus('success');
      this.#setBusy(false, button);
      this.dispatchEvent(new CustomEvent('newsletter-form:success', { bubbles: true }));
    } catch (error) {
      clearTimeout(timeout);
      // Log the operation and shape of the failure, never the address itself.
      console.warn('[newsletter-form] Klaviyo request failed, handing the form to Shopify', {
        listId: this.dataset.listId,
        timedOut: controller.signal.aborted,
        message: error instanceof Error ? error.message : String(error),
      });
      this.#fallbackToShopify(button);
    }
  };

  /**
   * Submit the form natively to Shopify's customer form. form.submit() does
   * not fire the submit event, so this is not intercepted again. The button
   * stays disabled because the page is about to navigate.
   * @param {Element | null} button
   */
  #fallbackToShopify(button) {
    try {
      this.#form.submit();
    } catch (error) {
      console.error('[newsletter-form] Shopify fallback submit failed', {
        message: error instanceof Error ? error.message : String(error),
      });
      this.#showStatus('error');
      this.#setBusy(false, button);
    }
  }

  #buildPayload(email, firstName) {
    const attributes = {
      email,
      subscriptions: {
        email: {
          marketing: {
            consent: 'SUBSCRIBED',
          },
        },
      },
    };
    if (firstName) attributes.first_name = firstName;

    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: this.dataset.source || 'Website newsletter form',
          profile: {
            data: {
              type: 'profile',
              attributes,
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: this.dataset.listId,
            },
          },
        },
      },
    };
  }

  /**
   * @param {boolean} busy
   * @param {Element | null} button
   */
  #setBusy(busy, button) {
    this.#busy = busy;
    this.toggleAttribute('aria-busy', busy);
    if (button instanceof HTMLButtonElement) button.disabled = busy;
  }

  /** @param {'success' | 'error' | null} state */
  #showStatus(state) {
    for (const el of this.querySelectorAll('[data-status]')) {
      const show = el.getAttribute('data-status') === state;
      el.toggleAttribute('hidden', !show);
      if (show && el instanceof HTMLElement) el.focus();
    }
  }
}

if (!customElements.get('newsletter-form-component')) {
  customElements.define('newsletter-form-component', NewsletterFormComponent);
}